<template>
  <div id="bank-details" class="fill-height align-start">
    <v-row class="g-container" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0 text-center">{{ $route.meta.title }}</h2>
      </v-col>

      <v-row no-gutters>
        <v-col cols="12">
          <v-card
            class="p-update-card mx-auto rounded-lg pa-4 px-md-10 py-md-6"
          >
            <v-form
              ref="form"
              v-model="bankDetailsFormState"
              :disabled="!editBankDetailsForm"
              @submit.prevent
            >
              <v-alert
                v-if="bankDetailsPersistenceWarning"
                class="mb-4"
                dense
                text
                outlined
                border="left"
                type="warning"
              >
                {{ bankDetailsPersistenceWarning }}
              </v-alert>

              <v-alert
                v-if="
                  pendingOnboardingRequirements.length > 0 &&
                  !showStripeOnboarding
                "
                class="mb-4"
                dense
                text
                outlined
                border="left"
                type="info"
              >
                Stripe still needs a few extra details before payouts can be
                enabled.
                <v-btn
                  small
                  text
                  color="primary"
                  class="ml-2"
                  @click="openStripeOnboarding()"
                >
                  Continue verification
                </v-btn>
              </v-alert>

              <template v-if="!userIsAllowedToEdit">
                <v-alert
                  dense
                  text
                  outlined
                  border="left"
                  elevation="1"
                  type="info"
                >
                  <div class="text-center">
                    Contact any <strong>Admin</strong> user to update details
                  </div>
                </v-alert>

                <div v-if="bankDetails" class="text-center mt-4">
                  <v-btn
                    rounded
                    text
                    class="i-btn-text-black"
                    @click="openStripeBalance()"
                  >
                    Check balance
                  </v-btn>
                </div>

                <v-divider class="mt-6 mx-0" horizontal></v-divider>
              </template>

              <template v-else>
                <v-row>
                  <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                    <span class="g-heading-s g-bold">
                      Bank Account
                      <v-icon
                        class="pb-1"
                        color="primary"
                        small
                        v-tooltip.right="
                          'This is the bank account payouts are paid to'
                        "
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </span>
                  </v-card-text>

                  <v-col class="py-0" cols="12">
                    <v-text-field
                      outlined
                      required
                      background-color="#F0F0F0"
                      dense
                      label="* BSB"
                      :value="bsb"
                      :loading="checkingBsb"
                      :error-messages="bsbErrorMessage"
                      @input="(bsb) => resolveBsb(bsb)"
                      :rules="[
                        require_value(bsb),
                        (v) =>
                          (!!v && bsbRegex.test(removeWhitespace(v))) ||
                          'BSB must be six digits'
                      ]"
                    ></v-text-field>
                  </v-col>

                  <v-alert
                    v-if="bsbBankName && bsbBankName.length > 0 && isValidBsb"
                    border="left"
                    class="ml-3 mt-n5 i-alert"
                    dense
                    text
                    outlined
                    elevation="1"
                    type="info"
                  >
                    {{ bsbBankName }}
                  </v-alert>

                  <v-col class="py-1" cols="12">
                    <v-text-field
                      outlined
                      required
                      background-color="#F0F0F0"
                      dense
                      label="* Account number"
                      :disabled="checkingBsb"
                      v-model="accountNumber"
                      :rules="[
                        require_value(accountNumber),
                        (v) =>
                          (!!v &&
                            accountNumberRegex.test(removeWhitespace(v))) ||
                          'Must be digits only'
                      ]"
                    ></v-text-field>
                  </v-col>

                  <v-card-text class="px-0 pt-0 pb-3 pb-md-6">
                    <span class="g-heading-s g-bold">
                      Accounting Email
                      <v-icon
                        class="pb-1"
                        color="primary"
                        small
                        v-tooltip.right="
                          'This is the email accounting related information will be sent to'
                        "
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </span>
                  </v-card-text>

                  <v-col class="py-1" cols="12">
                    <v-text-field
                      outlined
                      required
                      background-color="#F0F0F0"
                      dense
                      label="* Accounting Email"
                      :disabled="checkingBsb"
                      v-model="accountEmail"
                      maxlength="200"
                      :rules="rulesEmail"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-card-actions class="pa-0 justify-center">
                  <v-spacer v-if="!$vuetifyDisplay.smAndDown"></v-spacer>
                  <template v-if="!editBankDetailsForm">
                    <v-btn
                      v-if="bankDetails"
                      rounded
                      text
                      class="i-btn-text-black"
                      @click="openStripeBalance()"
                    >
                      Check balance
                    </v-btn>
                    <v-btn
                      v-if="userIsAllowedToEdit"
                      rounded
                      class="i-btn-block-secondary"
                      @click="startEditingBankDetailsForm()"
                    >
                      Edit details
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      rounded
                      text
                      class="i-btn-text-black"
                      @click="resetBankDetailsForm()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      rounded
                      class="i-btn-block-secondary"
                      :loading="updatingBankDetails"
                      :disabled="!isNewBankDetails || !bankDetailsFormState"
                      @click="submitBankDetailsForm()"
                    >
                      <span>Confirm</span>
                    </v-btn>
                  </template>
                </v-card-actions>
              </template>
            </v-form>
          </v-card>
        </v-col>
      </v-row>

      <v-dialog v-model="showStripeBalance" max-width="1240" scrollable>
        <v-card class="rounded-lg">
          <v-card-title class="justify-space-between">
            <span class="g-heading-s g-bold">Stripe balance</span>
            <v-btn icon @click="closeStripeBalance()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pb-0">
            <v-alert
              v-if="stripeBalanceError"
              dense
              text
              outlined
              type="error"
              border="left"
            >
              {{ stripeBalanceError }}
            </v-alert>

            <div v-if="stripeBalanceLoading" class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </div>

            <div v-show="!stripeBalanceLoading">
              <div
                ref="stripeNotificationBannerContainer"
                class="stripe-notification-banner-container mb-4"
              ></div>

              <v-tabs
                v-model="stripeBalanceTab"
                background-color="transparent"
                color="primary"
                grow
              >
                <v-tab href="#stripe-balances-tab">Balances</v-tab>
                <v-tab href="#stripe-payouts-list-tab">Payouts List</v-tab>
              </v-tabs>

              <v-tabs-items v-model="stripeBalanceTab" class="transparent">
                <v-tab-item value="stripe-balances-tab" eager>
                  <div
                    ref="stripeBalancesContainer"
                    class="stripe-component-container"
                  ></div>
                </v-tab-item>
                <v-tab-item value="stripe-payouts-list-tab" eager>
                  <div
                    ref="stripePayoutsListContainer"
                    class="stripe-component-container"
                  ></div>
                </v-tab-item>
              </v-tabs-items>
            </div>
          </v-card-text>

          <v-card-actions class="justify-end px-6 pb-6 pt-2">
            <v-btn text class="i-btn-text-black" @click="closeStripeBalance()">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="showStripeOnboarding"
        max-width="980"
        persistent
        scrollable
      >
        <v-card class="rounded-lg">
          <v-card-title class="justify-space-between">
            <span class="g-heading-s g-bold">Finish Stripe verification</span>
            <v-btn icon @click="closeStripeOnboarding()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pb-0">
            <v-alert dense text outlined type="info" border="left">
              Your bank details were saved. Stripe still needs a few extra
              details to enable payouts.
            </v-alert>

            <v-alert
              v-if="stripeOnboardingError"
              dense
              text
              outlined
              type="error"
              border="left"
            >
              {{ stripeOnboardingError }}
            </v-alert>

            <div v-if="stripeOnboardingLoading" class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </div>

            <div
              v-show="!stripeOnboardingLoading"
              ref="stripeOnboardingContainer"
              class="stripe-onboarding-container"
            ></div>
          </v-card-text>

          <v-card-actions class="justify-end px-6 pb-6 pt-2">
            <v-btn
              text
              class="i-btn-text-black"
              @click="closeStripeOnboarding()"
            >
              Close
            </v-btn>
            <v-btn
              rounded
              class="i-btn-block-secondary"
              :loading="refreshingAfterOnboarding"
              @click="refreshAfterStripeOnboarding()"
            >
              Refresh status
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import { settings } from '@/utils/settings'
import {
  createBankDetailsBalanceSession,
  createBankDetailsOnboardingSession,
  fetchBankDetails,
  fetchBsb,
  refreshBankDetailsStatus,
  updateBankDetails
} from '@/services/bank'
import FieldValidators from '@/utils/FieldValidators'

const BSB_REGEX = /^\d{3}-?\d{3}$/
const ACCOUNT_NUMBER_REGEX = /^\d+$/
const STRIPE_CONNECT_SCRIPT_ID = 'stripe-connect-js'
const STRIPE_CONNECT_SCRIPT_URL =
  'https://connect-js.stripe.com/v1.0/connect.js'
export default {
  components: {},

  data: () => ({
    bsbRegex: BSB_REGEX,
    accountNumberRegex: ACCOUNT_NUMBER_REGEX,
    bankDetailsFormState: false,
    editBankDetailsForm: false,
    bankDetails: null,
    bsb: null,
    accountNumber: null,
    accountEmail: null,
    updatingBankDetails: false,
    refreshingAfterOnboarding: false,
    showStripeOnboarding: false,
    stripeOnboardingLoading: false,
    stripeOnboardingError: null,
    showStripeBalance: false,
    stripeBalanceLoading: false,
    stripeBalanceError: null,
    stripeBalanceTab: 'stripe-balances-tab',
    stripeConnectInstance: null,
    stripeOnboardingElement: null,
    stripeNotificationBannerElement: null,
    stripeBalancesElement: null,
    stripePayoutsListElement: null,
    pendingOnboardingRequirements: [],
    bankDetailsPersistenceWarning: null,
    ...FieldValidators,
    checkingBsb: false,
    bsbBankName: null,
    bsbErrorMessage: null
  }),

  computed: {
    ...mapGetters('preferences', ['isOwnerUser', 'isAdminUser']),
    ...mapGetters('profile', ['readyToTrade']),
    ...mapGetters('tenant', ['featureBankAccountsEnabled']),
    ...mapState('preferences', ['selectedSupplierId']),

    isNewBankDetails() {
      return (
        !_.isEqual(
          this.toDigitsOnly(this.accountNumber),
          this.toDigitsOnly(this.bankDetails?.accountNumber)
        ) ||
        !_.isEqual(
          this.toDigitsOnly(this.bsb),
          this.toDigitsOnly(this.bankDetails?.bsb)
        ) ||
        !_.isEqual(this.accountEmail?.trim(), this.bankDetails?.accountEmail)
      )
    },

    userIsAllowedToEdit() {
      return this.isAdminUser
    },

    isValidBsb() {
      return !!this.bsb && BSB_REGEX.test(this.bsb)
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('profile', ['fetchSupplierProfile']),

    removeWhitespace(bsb) {
      return bsb?.replace(/\s/g, '') // replace all whitespace
    },

    toDigitsOnly(value) {
      return value?.replace(/[^0-9]+/g, '') // replace anything that is not a number
    },

    submitBankDetailsForm() {
      this.updatingBankDetails = true
      this.bankDetailsPersistenceWarning = null

      const request = {
        accountNumber: this.toDigitsOnly(this.accountNumber),
        bsb: this.toDigitsOnly(this.bsb),
        accountEmail: this.accountEmail.trim()
      }

      updateBankDetails(request)
        .then(async (response) => {
          console.info('[BankDetails] Save response received', {
            persistedInCore: response?.persistedInCore,
            onboardingRequired: response?.onboarding?.required,
            onboardingType: response?.onboarding?.type,
            onboardingRequirements: response?.onboarding?.requirements,
            connectedAccountId: response?.connectedAccountId
          })
          this.applySavedBankDetails(response)
          this.addNotification({
            color: 'secondary',
            text: 'Bank Details updated successfully'
          })
          await Promise.all([
            this.fetchSupplierProfile(this.selectedSupplierId),
            this.resetBankDetailsForm()
          ])
          if (!response?.persistedInCore) {
            this.bankDetailsPersistenceWarning =
              response?.message ||
              'Bank details were saved in Stripe, but the platform state has not refreshed yet.'
          }
          if (response?.onboarding?.required) {
            this.pendingOnboardingRequirements =
              response.onboarding.requirements || []
            console.info(
              '[BankDetails] Opening Stripe onboarding because requirements are still pending',
              {
                requirements: this.pendingOnboardingRequirements
              }
            )
            await this.openStripeOnboarding()
          } else {
            console.info(
              '[BankDetails] Stripe onboarding not required after bank save'
            )
            this.pendingOnboardingRequirements = []
          }
          // For when is new Supplier and has not finished setup steps
          if (!this.readyToTrade && !response?.onboarding?.required) {
            this.$router.push({
              name: 'dashboard',
              query: { showTradingSteps: 'true' }
            })
          }
        })
        .catch((err) => {
          console.error('[BankDetails] Bank detail save failed', err)
          this.addNotification({
            color: 'error',
            text: 'Bank details could not be updated, please try again...'
          })
        })
        .finally(() => {
          this.updatingBankDetails = false
        })
    },

    async openStripeOnboarding() {
      console.info('[BankDetails] Opening Stripe onboarding modal')
      this.showStripeOnboarding = true
      this.stripeOnboardingLoading = true
      this.stripeOnboardingError = null

      try {
        await this.$nextTick()
        await this.mountStripeOnboarding()
      } catch (err) {
        console.error(
          '[BankDetails] Stripe onboarding modal failed to open',
          err
        )
        this.stripeOnboardingError =
          'Stripe verification could not be opened right now. Please try again.'
      } finally {
        this.stripeOnboardingLoading = false
      }
    },

    async openStripeBalance() {
      console.info('[BankDetails] Opening Stripe balance modal')
      this.showStripeBalance = true
      this.stripeBalanceLoading = true
      this.stripeBalanceError = null

      try {
        await this.$nextTick()
        await this.mountStripeBalance()
      } catch (err) {
        console.error('[BankDetails] Stripe balance modal failed to open', err)
        this.stripeBalanceError =
          'Stripe balance could not be opened right now. Please try again.'
      } finally {
        this.stripeBalanceLoading = false
      }
    },

    async mountStripeOnboarding() {
      const container = this.$refs.stripeOnboardingContainer
      if (!container) {
        console.warn('[BankDetails] Stripe onboarding container is missing')
        return
      }

      container.innerHTML = ''

      this.stripeConnectInstance = await this.createStripeConnectInstance(
        async () => {
          console.info(
            '[BankDetails] Requesting Stripe onboarding session client secret'
          )
          const session = await createBankDetailsOnboardingSession()
          console.info('[BankDetails] Stripe onboarding session received', {
            connectedAccountId: session?.connectedAccountId,
            clientSecretPresent: !!session?.clientSecret
          })
          return session.clientSecret
        }
      )

      this.stripeOnboardingElement =
        this.stripeConnectInstance.create('account-onboarding')
      console.info(
        '[BankDetails] Stripe account-onboarding component created and mounting'
      )
      container.appendChild(this.stripeOnboardingElement)
    },

    async mountStripeBalance() {
      const bannerContainer = this.$refs.stripeNotificationBannerContainer
      const balancesContainer = this.$refs.stripeBalancesContainer
      const payoutsListContainer = this.$refs.stripePayoutsListContainer
      if (!bannerContainer || !balancesContainer || !payoutsListContainer) {
        console.warn('[BankDetails] Stripe balance containers are missing')
        return
      }

      bannerContainer.innerHTML = ''
      balancesContainer.innerHTML = ''
      payoutsListContainer.innerHTML = ''

      this.stripeConnectInstance = await this.createStripeConnectInstance(
        async () => {
          console.info(
            '[BankDetails] Requesting Stripe balance session client secret'
          )
          const session = await createBankDetailsBalanceSession()
          console.info('[BankDetails] Stripe balance session received', {
            connectedAccountId: session?.connectedAccountId,
            clientSecretPresent: !!session?.clientSecret
          })
          return session.clientSecret
        }
      )

      this.stripeNotificationBannerElement = this.stripeConnectInstance.create(
        'notification-banner'
      )
      this.stripeBalancesElement = this.stripeConnectInstance.create('balances')
      this.stripePayoutsListElement =
        this.stripeConnectInstance.create('payouts-list')
      console.info(
        '[BankDetails] Stripe embedded supplier components created and mounting'
      )
      bannerContainer.appendChild(this.stripeNotificationBannerElement)
      balancesContainer.appendChild(this.stripeBalancesElement)
      payoutsListContainer.appendChild(this.stripePayoutsListElement)
    },

    async createStripeConnectInstance(fetchClientSecret) {
      const stripeConnect = await this.loadStripeConnect()
      console.info(
        '[BankDetails] Stripe Connect.js loaded, initialising embedded component session'
      )
      return stripeConnect.init({
        publishableKey: settings.STRIPE_PUBLIC_KEY,
        appearance: {
          overlays: 'dialog'
        },
        fetchClientSecret
      })
    },

    loadStripeConnect() {
      if (window.StripeConnect?.init) {
        console.info(
          '[BankDetails] Reusing existing Stripe Connect.js instance'
        )
        return Promise.resolve(window.StripeConnect)
      }

      return new Promise((resolve, reject) => {
        const existingScript = document.getElementById(STRIPE_CONNECT_SCRIPT_ID)
        if (existingScript) {
          console.info(
            '[BankDetails] Waiting for existing Stripe Connect.js script tag to finish loading'
          )
          existingScript.addEventListener('load', () =>
            resolve(window.StripeConnect)
          )
          existingScript.addEventListener('error', reject)
          return
        }

        console.info('[BankDetails] Injecting Stripe Connect.js script tag')
        const script = document.createElement('script')
        script.id = STRIPE_CONNECT_SCRIPT_ID
        script.src = STRIPE_CONNECT_SCRIPT_URL
        script.async = true
        script.onload = () => {
          if (window.StripeConnect?.init) {
            resolve(window.StripeConnect)
          } else {
            reject(new Error('Stripe Connect failed to initialize'))
          }
        }
        script.onerror = reject
        document.head.appendChild(script)
      })
    },

    closeStripeOnboarding() {
      console.info('[BankDetails] Closing Stripe onboarding modal')
      this.showStripeOnboarding = false
      this.stripeOnboardingError = null
      if (this.$refs.stripeOnboardingContainer) {
        this.$refs.stripeOnboardingContainer.innerHTML = ''
      }
      this.stripeOnboardingElement = null
    },

    closeStripeBalance() {
      console.info('[BankDetails] Closing Stripe balance modal')
      this.showStripeBalance = false
      this.stripeBalanceError = null
      this.stripeBalanceTab = 'stripe-balances-tab'
      if (this.$refs.stripeNotificationBannerContainer) {
        this.$refs.stripeNotificationBannerContainer.innerHTML = ''
      }
      if (this.$refs.stripeBalancesContainer) {
        this.$refs.stripeBalancesContainer.innerHTML = ''
      }
      if (this.$refs.stripePayoutsListContainer) {
        this.$refs.stripePayoutsListContainer.innerHTML = ''
      }
      this.stripeNotificationBannerElement = null
      this.stripeBalancesElement = null
      this.stripePayoutsListElement = null
    },

    async refreshAfterStripeOnboarding() {
      console.info(
        '[BankDetails] Refreshing supplier payment status after Stripe onboarding'
      )
      this.refreshingAfterOnboarding = true
      try {
        await refreshBankDetailsStatus()
        await Promise.all([
          this.fetchSupplierProfile(this.selectedSupplierId),
          this.resetBankDetailsForm()
        ])
        this.pendingOnboardingRequirements = []
        this.addNotification({
          color: 'secondary',
          text: 'Supplier payment status refreshed successfully'
        })
        this.closeStripeOnboarding()
      } catch (err) {
        console.error(
          '[BankDetails] Refresh after Stripe onboarding failed',
          err
        )
        this.addNotification({
          color: 'error',
          text: 'Unable to refresh supplier payment status right now'
        })
      } finally {
        this.refreshingAfterOnboarding = false
      }
    },

    startEditingBankDetailsForm() {
      this.editBankDetailsForm = true
      if (this.bankDetails?.accountNumber?.includes('*')) {
        this.accountNumber = null
      }
    },

    applySavedBankDetails(response) {
      if (!response?.bankDetails) return

      this.bankDetails = response.bankDetails
      this.bsb = response.bankDetails.bsb
      this.accountNumber = response.bankDetails.accountNumber
      this.accountEmail = response.bankDetails.accountEmail
      this.editBankDetailsForm = false
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },

    resetBankDetailsForm() {
      this.bsbBankName = null
      return fetchBankDetails()
        .then((res) => {
          this.bankDetails = res
          this.bsb = res.bsb
          this.accountNumber = res.accountNumber
          this.accountEmail = res.accountEmail
          if (this.$refs.form) {
            // in case this is the very first time a supplier enters their details (which means there is no data yet), we don't want to show all the fields in error
            this.$refs.form.resetValidation()
          }
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.editBankDetailsForm = !this.bankDetails
        })
    },

    resolveBsb(rawBsb) {
      this.bsb = this.removeWhitespace(rawBsb)
      this.bsbErrorMessage = null
      this.bsbBankName = null

      // Only validate in backend once has passed frontend validations
      if (this.isValidBsb) {
        this.checkingBsb = true

        fetchBsb(this.bsb)
          .then((res) => {
            if (res && res?.bsbName) {
              this.bsbBankName = res.bsbDisplayName
            } else {
              this.bsbErrorMessage =
                'Unknown BSB. Please contact support@flewidtech.com if you are sure your BSB is correct'
            }
          })
          .catch((err) => {
            // Validation APIs have request limiters
            const errMessage =
              err.response.status === 429
                ? 'We detected too many BSB resolution attempts. Please wait a few minutes before trying again.'
                : 'BSB resolution not completed, please try again...'

            this.addNotification({
              color: 'error',
              text: errMessage,
              timeout: -1
            })
          })
          .finally(() => {
            this.checkingBsb = false
          })
      }
    }
  },

  mounted() {
    // Note: this is a simple guard to ensure that ILRMarketplace users can't browse to this page directly.
    // It is already hidden on the Account.vue landing page, so if changing the behaviour there you will need to change it here too!
    if (!this.featureBankAccountsEnabled)
      this.$router.push({ name: 'dashboard' })

    this.resetBankDetailsForm()
  }
}
</script>

<style lang="scss" scoped>
#bank-details {
  hr {
    min-width: 100%;
    margin-top: 10px;
    margin-bottom: 30px;
  }

  .p-update-card {
    max-width: 740px;
  }

  .mdi {
    color: black;
  }

  .stripe-onboarding-container {
    min-height: 520px;
  }

  .stripe-notification-banner-container {
    min-height: 48px;
  }

  .stripe-component-container {
    min-height: 560px;
    padding-top: 16px;
  }
}
</style>
